class pendingRequest {
	constructor(id, promiseAccept, promiseReject) {
		this.id = id;
		this.accept = promiseAccept;
		this.reject = promiseReject;
	}
}

/**
 * @class cachedResult
 * @template T
 * @property {T} item
 * @property {Number} time
 */
class cachedResult {
	constructor(item, time) {
		this.item = item;
		this.time = time;
	}

	getAge() {
		return Date.now() - this.time;
	}
}

/**
 * @typedef {Object} ListResult
 * @template T
 * @property {Number} [Total]
 * @property {T[]} Items
 */

/**
 * @callback requestFunction
 * @template T
 * @async
 * @param {Number[]} ids
 * @returns ListResult<T>
 */

/**
 * @typedef {Object} batchRequestOptions
 * @template T
 * @property {requestFunction<T>} requestFunction
 * @property {String|Function} [resultId]
 * @property {Number} [debounceTime]
 * @property {Boolean} [cache]
 * @property {Number} [cacheExpiry]
 * @property {Number} [limitSize]
 */

/**
 * @class BatchRequest
 * @template T
 * @property {requestFunction<T>} requestFunction
 * @property {String|Function} resultId
 * @property {Number} debounceTime
 * @property {Number} timeoutId
 * @property {Boolean} shouldCache
 * @property {Number} cacheExpiry Time to cache expiration in milliseconds
 * @property {Number} limitSize Max accumulated elements to force send request
 * @property {Object} cache
 * @property {pendingRequest[]} requests
 */
export default class BatchRequest {
	/**
	 *
	 * @param {batchRequestOptions} options
	 */
	constructor(options) {
		options = Object.assign({
			resultId: 'Id',
			debounceTime: 100,
			cache: false,
			cacheExpiry: 0,
			limitSize: 100
		}, options);

		this.requests = [];

		this.requestFunction = options.requestFunction;
		this.resultId = options.resultId;

		this.debounceTime = options.debounceTime;
		this.timeoutId = null;

		this.shouldCache = options.cache;
		this.cacheExpiry = options.cacheExpiry;

		this.limitSize = options.limitSize;

		this.cache = {};
	}

	/**
	 * @param {pendingRequest} request
	 */
	addRequest(request) {
		this.requests.push(request);
		this.resetDebouncer();
	}

	resetDebouncer() {
		if (this.timeoutId) clearTimeout(this.timeoutId);
		if(this.requests.length >= this.limitSize) {
			this.run();
		}
		else {
			this.timeoutId = setTimeout(() => this.run(), this.debounceTime);
		}
	}

	getId(item) {
		return this.resultId instanceof Function ? this.resultId(item) : item[this.resultId];
	}

	async run() {
		const list = this.requests.slice(0);
		this.requests = [];

		let ids = list.map(x => x.id).filter((x, index, arr) => arr.indexOf(x) === index);

		if (!ids.length) return;

		try {
			/** @type {ListResult} **/
			let result = await this.requestFunction(ids);

			for (let item of result.Items) {
				let id = this.getId(item);

				this.addToCache(id, item);

				let requests = list.filter(x => x.id === id);

				for (let request of requests) {
					list.remove(request);

					try {
						request.accept(item);
					} catch (e) {
						console.error(e);
					}
				}
			}
		} catch (e) {
			// The request itself failed, reject all
			for (let request of list) {
				if (ids.indexOf(request.id) < 0) continue;

				request.reject(e);
			}
		}
	}

	addToCache(id, item) {
		if (this.shouldCache) {
			this.cache[id] = new cachedResult(item, Date.now);
		}
	}

	/**
	 *
	 * @param id
	 * @returns {cachedResult<T>}
	 */
	getFromCache(id) {
		if (this.shouldCache && this.cache.hasOwnProperty(id)) {
			if (this.cache[id].getAge() < this.cacheExpiry) {
				return this.cache[id];
			} else {
				delete this.cache[id];
				return null;
			}
		}

		return null;
	}

	/**
	 *
	 * @param id
	 * @returns {Promise<T>}
	 */
	async fetch(id) {
		if (!id) return null;

		let cachedResult = this.getFromCache(id);

		if (cachedResult) {
			return cachedResult.item;
		}

		return new Promise((accept, reject) => {
			this.addRequest(new pendingRequest(id, accept, reject));
		});
	}
}